import { CalendarIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormatters } from '@/shared/i18n/format';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Calendar } from '@/shared/ui/calendar';
import { Input } from '@/shared/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';

export interface DateTimePickerProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  /** Include a time input (default true). Without it the time part is 00:00 local. */
  withTime?: boolean;
  /** Minute granularity of the time input (default 5). */
  minuteStep?: number;
  /** Time applied when a day is picked and no time was set yet (HH:mm, default 09:00). */
  defaultTime?: string;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');
const toTimeString = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

function withTimeOf(day: Date, time: string): Date {
  const [h = 0, m = 0] = time.split(':').map(Number);
  const result = new Date(day);
  result.setHours(h, m, 0, 0);
  return result;
}

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Date (and optionally time) picker in a popover; locale and week start follow the UI language. */
export function DateTimePicker({
  value,
  onChange,
  withTime = true,
  minuteStep = 5,
  defaultTime = '09:00',
  minDate,
  maxDate,
  placeholder,
  clearable = true,
  disabled,
  invalid,
  id,
  className,
  ...aria
}: DateTimePickerProps) {
  const { t } = useTranslation('shared');
  const fmt = useFormatters();
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date | undefined>(value ?? undefined);

  const label = value ? fmt.date(value, withTime ? 'dateTime' : 'date') : null;

  const selectDay = (day: Date | undefined) => {
    if (!day) return;
    onChange(withTime ? withTimeOf(day, value ? toTimeString(value) : defaultTime) : startOfDay(day));
    if (!withTime) setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setMonth(value ?? undefined);
      }}
    >
      <div className={cn('relative inline-flex w-full min-w-0', className)}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-label={aria['aria-label']}
            className={cn(
              'w-full justify-start px-3 font-normal tabular-nums',
              !value && 'text-subtle-foreground',
              clearable && value && 'pr-8',
            )}
          >
            <CalendarIcon className="text-muted-foreground" />
            <span className="truncate">
              {label ?? placeholder ?? t(withTime ? 'date.pickDateTime' : 'date.pickDate')}
            </span>
          </Button>
        </PopoverTrigger>
        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label={t('actions.clear')}
            className="absolute top-1/2 right-2 inline-flex size-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <XIcon className="size-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={selectDay}
          month={month}
          onMonthChange={setMonth}
          disabled={[...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])]}
          autoFocus
        />
        <div className="flex items-center gap-2 border-t p-3">
          {withTime && (
            <Input
              type="time"
              step={minuteStep * 60}
              aria-label={t('date.time')}
              className="h-8 w-28 tabular-nums"
              value={value ? toTimeString(value) : ''}
              onChange={(e) => {
                if (!e.target.value) return;
                onChange(withTimeOf(value ?? new Date(), e.target.value));
              }}
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={() => {
              const today = new Date();
              setMonth(today);
              selectDay(today);
            }}
          >
            {t('date.today')}
          </Button>
          {withTime && (
            <Button size="sm" onClick={() => setOpen(false)}>
              {t('actions.done')}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
